/**
 * PushRegistration — the device's FCM push token, end to end (2026-09-08).
 *
 * Contract:
 *   • NO mount-time call. `registerPush()` is the GESTURE path — reached only
 *     through notificationCapability.requestPushPermission (a click), after
 *     the browser said 'granted'. `refreshPushRegistrationIfGranted()` is the
 *     SILENT path — it does nothing unless permission is already granted AND
 *     a user is signed in; GlobalChatSystem calls it after auth resolves, on
 *     tab return, and daily, so a rotated token or a fresh sign-in on a device
 *     that already allowed notifications re-registers without a prompt.
 *   • The messaging service worker is ONE fixed-URL file
 *     (public/firebase-messaging-sw.js — cache-doctrine ALWAYS_FRESH). It
 *     receives the public Firebase config on its registration URL:
 *     `/firebase-messaging-sw.js?cfg=<base64 JSON>` — no postMessage handshake
 *     nobody sends.
 *   • The token lives at users/{uid}/device_tokens/{token} with the device
 *     facts the fan-out and the Settings device list need; the doc is deleted
 *     on sign-out (before Firebase signs out — cleanupManager's callback runs
 *     first; siteDataWipe's service-worker unregister path calls the same
 *     idempotent revoke) and when FCM says the token is dead.
 */
import { deleteToken, getMessaging, getToken, isSupported, onMessage, type Messaging } from 'firebase/messaging';
import { deleteDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import app from '../utils/firebase';
import { Paths } from '../repositories/firestoreUtils';
import { registerCleanupCallback } from '../utils/cleanupManager';
import {
  MESSAGING_SW_PATH,
  MESSAGING_SW_SCOPE,
  detectNotificationPlatform,
  readNotificationEnvironment,
  type NotificationPlatform,
} from './notificationCapability';

/** Silent refresh cadence GlobalChatSystem schedules (one day). */
export const PUSH_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000;
/** Visibility-driven refreshes closer than this reuse the last result. */
export const PUSH_REFRESH_MIN_GAP_MS = 60 * 1000;

const TOKEN_CACHE_KEY = 'prest0_push_device:v1';
/** FCM error codes that mean "this token is gone — drop its doc". */
const DEAD_TOKEN_CODES = new Set([
  'messaging/registration-token-not-registered',
  'messaging/token-unsubscribe-failed',
  'messaging/invalid-argument',
]);

export interface CachedPushRegistration {
  uid: string;
  token: string;
  at: number;
}

export interface DeviceDescription {
  platform: NotificationPlatform;
  os: 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'chromeos' | 'other';
  browser: 'safari' | 'chrome' | 'edge' | 'firefox' | 'samsung' | 'other';
}

/** The public Firebase config keys the service worker needs (all REACT_APP_* — public by construction). */
const PUBLIC_CONFIG_KEYS = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'] as const;

/** `/firebase-messaging-sw.js?cfg=<base64 JSON>` — same filename, config on the query. */
export function buildMessagingSwUrl(config: Record<string, unknown>): string {
  const pub: Record<string, string> = {};
  for (const key of PUBLIC_CONFIG_KEYS) {
    const v = config[key];
    if (typeof v === 'string' && v) pub[key] = v;
  }
  const json = JSON.stringify(pub);
  const b64 = typeof btoa === 'function'
    ? btoa(json)
    : Buffer.from(json, 'utf8').toString('base64');
  return `${MESSAGING_SW_PATH}?cfg=${encodeURIComponent(b64)}`;
}

export function describeDevice(ua: string, maxTouchPoints = 0): DeviceDescription {
  const platform = detectNotificationPlatform({ ua, maxTouchPoints });
  let os: DeviceDescription['os'] = 'other';
  if (platform === 'ios-safari') os = 'ios';
  else if (/Android/i.test(ua)) os = 'android';
  else if (/CrOS/i.test(ua)) os = 'chromeos';
  else if (/Windows/i.test(ua)) os = 'windows';
  else if (/Macintosh|Mac OS X/i.test(ua)) os = 'macos';
  else if (/Linux/i.test(ua)) os = 'linux';
  let browser: DeviceDescription['browser'] = 'other';
  if (/SamsungBrowser\//i.test(ua)) browser = 'samsung';
  else if (/Edg\//i.test(ua)) browser = 'edge';
  else if (/Firefox\/|FxiOS\//i.test(ua)) browser = 'firefox';
  else if (/Chrome\/|CriOS\/|Chromium\//i.test(ua)) browser = 'chrome';
  else if (/Safari\//i.test(ua)) browser = 'safari';
  return { platform, os, browser };
}

/** The running bundle's identity — the main chunk's content hash (the same oracle asset-manifest serves). */
export function readAppVersion(): string {
  try {
    if (typeof document === 'undefined') return 'unknown';
    const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'));
    for (const s of scripts) {
      const m = /\/static\/js\/(main\.[0-9a-f]+)\.js/i.exec(s.src || '');
      if (m) return m[1];
    }
  } catch { /* no DOM */ }
  return 'unknown';
}

export function readCachedPushRegistration(): CachedPushRegistration | null {
  try {
    const raw = localStorage.getItem(TOKEN_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedPushRegistration>;
    if (typeof parsed.uid !== 'string' || typeof parsed.token !== 'string' || !parsed.uid || !parsed.token) return null;
    return { uid: parsed.uid, token: parsed.token, at: typeof parsed.at === 'number' ? parsed.at : 0 };
  } catch {
    return null;
  }
}

function writeCachedPushRegistration(entry: CachedPushRegistration | null): void {
  try {
    if (entry) localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(entry));
    else localStorage.removeItem(TOKEN_CACHE_KEY);
  } catch { /* storage unavailable — the doc is still the truth */ }
}

/** True when this device holds a push token for THIS account. */
export function hasPushTokenForUid(uid: string | null | undefined): boolean {
  const cached = readCachedPushRegistration();
  return Boolean(uid && cached && cached.uid === uid);
}

let vapidWarned = false;
function readVapidKey(): string | undefined {
  const key = (process.env.REACT_APP_FCM_VAPID_KEY || '').trim();
  if (key) return key;
  // The web-push key is an owner step (GitHub Actions secret
  // REACT_APP_FCM_VAPID_KEY → deploy.yml). Warn once, in dev only — tokens
  // still mint on Firebase's default key, so nothing here is fatal.
  if (!vapidWarned && process.env.NODE_ENV !== 'production') {
    vapidWarned = true;
    // eslint-disable-next-line no-console
    console.warn('[push] REACT_APP_FCM_VAPID_KEY is not set — web push runs on the default key until the deploy secret exists.');
  }
  return undefined;
}

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

/** Register (once per page) the messaging worker with the config on its URL. */
export function ensureMessagingServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return Promise.resolve(null);
  if (!registrationPromise) {
    const url = buildMessagingSwUrl((app.options || {}) as Record<string, unknown>);
    registrationPromise = navigator.serviceWorker
      .register(url, { scope: MESSAGING_SW_SCOPE })
      .catch(() => null);
  }
  return registrationPromise;
}

/** The worker's registration if it exists (no registration side effect). */
export async function getMessagingServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  if (registrationPromise) {
    const reg = await registrationPromise;
    if (reg) return reg;
  }
  try {
    return (await navigator.serviceWorker.getRegistration(MESSAGING_SW_SCOPE)) || null;
  } catch {
    return null;
  }
}

let swListenersInstalled = false;
/** The worker asks the page to re-register after a pushsubscriptionchange. */
function installServiceWorkerListeners(): void {
  if (swListenersInstalled || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  swListenersInstalled = true;
  try {
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      const data = event.data as { type?: string } | null;
      if (data && data.type === 'PUSH_RESUBSCRIBE') void refreshPushRegistrationIfGranted({ force: true });
    });
  } catch { /* no container */ }
}

let foregroundInstalled = false;
function installForegroundHandler(messaging: Messaging): void {
  if (foregroundInstalled) return;
  foregroundInstalled = true;
  try {
    // A push that lands while the tab is visible: the in-app chime + badges
    // already cover it (the thread index bump). Surface the payload on a
    // window event for any future foreground surface; no UI here.
    onMessage(messaging, (payload) => {
      try {
        window.dispatchEvent(new CustomEvent('prest0n:push-foreground', { detail: payload }));
      } catch { /* noop */ }
    });
  } catch { /* messaging unavailable */ }
}

function errorCode(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : '';
}

/** Drop the doc for a token FCM declared dead. */
async function pruneDeadToken(uid: string, token: string): Promise<void> {
  try { await deleteDoc(Paths.userDeviceTokenDoc(uid, token)); } catch { /* best-effort */ }
  const cached = readCachedPushRegistration();
  if (cached && cached.token === token) writeCachedPushRegistration(null);
}

async function writeDeviceTokenDoc(uid: string, token: string, isNew: boolean): Promise<void> {
  const env = readNotificationEnvironment();
  const device = describeDevice(env.ua, env.maxTouchPoints);
  const doc: Record<string, unknown> = {
    token,
    platform: device.platform,
    os: device.os,
    browser: device.browser,
    standalone: env.standalone,
    ua: env.ua.slice(0, 512),
    permission: 'granted',
    app_version: readAppVersion(),
    last_seen: serverTimestamp(),
  };
  if (isNew) doc.created_at = serverTimestamp();
  await setDoc(Paths.userDeviceTokenDoc(uid, token), doc, { merge: true });
}

let inFlight: Promise<string | null> | null = null;
let lastRefreshAt = 0;

async function acquireAndStoreToken(uid: string): Promise<string | null> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
  let supported = false;
  try { supported = await isSupported(); } catch { supported = false; }
  if (!supported) return null;
  const reg = await ensureMessagingServiceWorker();
  if (!reg) return null;
  installServiceWorkerListeners();
  const messaging = getMessaging(app);
  const cached = readCachedPushRegistration();
  let token: string | null = null;
  try {
    token = await getToken(messaging, { vapidKey: readVapidKey(), serviceWorkerRegistration: reg });
  } catch (err) {
    if (cached && DEAD_TOKEN_CODES.has(errorCode(err))) await pruneDeadToken(cached.uid, cached.token);
    return null;
  }
  if (!token) return null;
  // A rotated token, or the same device signed into another account: the old
  // doc must not keep receiving this account's pushes.
  if (cached && (cached.token !== token || cached.uid !== uid)) {
    try { await deleteDoc(Paths.userDeviceTokenDoc(cached.uid, cached.token)); } catch { /* best-effort */ }
  }
  const isNew = !cached || cached.token !== token || cached.uid !== uid;
  try {
    await writeDeviceTokenDoc(uid, token, isNew);
  } catch {
    return null;
  }
  writeCachedPushRegistration({ uid, token, at: Date.now() });
  installForegroundHandler(messaging);
  lastRefreshAt = Date.now();
  return token;
}

function runOnce(uid: string): Promise<string | null> {
  if (inFlight) return inFlight;
  inFlight = acquireAndStoreToken(uid)
    .catch(() => null)
    .finally(() => { inFlight = null; });
  return inFlight;
}

/**
 * GESTURE path — permission is already 'granted' (notificationCapability
 * asked inside the click). Requires a signed-in user; never prompts.
 */
export async function registerPush(): Promise<string | null> {
  const uid = getAuth().currentUser?.uid || null;
  if (!uid) return null;
  return runOnce(uid);
}

/**
 * SILENT path — only when permission is already granted AND a user is signed
 * in. Safe to call often: throttled to one attempt per PUSH_REFRESH_MIN_GAP_MS
 * unless `force` (a pushsubscriptionchange, a fresh sign-in).
 */
export async function refreshPushRegistrationIfGranted(opts?: { force?: boolean }): Promise<string | null> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;
  const uid = getAuth().currentUser?.uid || null;
  if (!uid) return null;
  const cached = readCachedPushRegistration();
  const sameAccount = cached?.uid === uid;
  if (!opts?.force && sameAccount && Date.now() - lastRefreshAt < PUSH_REFRESH_MIN_GAP_MS) {
    return cached?.token ?? null;
  }
  return runOnce(uid);
}

/**
 * Forget this device: delete the token doc (needs the still-signed-in user —
 * hence the cleanupManager callback below, which runs BEFORE Firebase signs
 * out), invalidate the FCM token, clear the local cache. Idempotent; every
 * step is best-effort so a sign-out can never hang on it.
 */
export async function unregisterPushDevice(): Promise<void> {
  const cached = readCachedPushRegistration();
  writeCachedPushRegistration(null);
  if (cached) {
    try { await deleteDoc(Paths.userDeviceTokenDoc(cached.uid, cached.token)); } catch { /* signed out already, or offline */ }
  }
  try {
    if (await isSupported()) await deleteToken(getMessaging(app));
  } catch { /* no token to delete */ }
}

/** Remove ONE registered device from the account (the Settings device list). */
export async function removeDeviceToken(uid: string, token: string): Promise<void> {
  await deleteDoc(Paths.userDeviceTokenDoc(uid, token));
  const cached = readCachedPushRegistration();
  if (cached && cached.token === token) {
    writeCachedPushRegistration(null);
    try {
      if (await isSupported()) await deleteToken(getMessaging(app));
    } catch { /* best-effort */ }
  }
}

// Sign-out hook: cleanupManager.performCompleteCleanup runs in AuthContext's
// logout BEFORE getAuth().signOut(), so the doc delete still has the owner's
// auth. Module-level registration (the userDataIsolation idiom).
if (typeof window !== 'undefined') {
  try {
    registerCleanupCallback(() => unregisterPushDevice());
  } catch { /* cleanup manager unavailable */ }
}

export default { registerPush, refreshPushRegistrationIfGranted, unregisterPushDevice };
